import { Loader2, UserCog } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useReturnToMyAccount } from "@/lib/impersonation";
import { useToast } from "@/hooks/use-toast";

/**
 * Persistent, app-wide banner shown whenever the active session is an admin
 * "Login as" impersonation. Visible on every screen (member-facing and
 * admin-facing alike) so it's never possible to forget you're impersonating.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const { toast } = useToast();
  const returnToMyAccount = useReturnToMyAccount();

  if (!user?.impersonatedBy) return null;

  const admin = user.impersonatedBy;

  return (
    <div
      className="sticky top-0 z-[100] flex flex-wrap items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950 shadow-md"
      role="alert"
      data-testid="banner-impersonation"
    >
      <UserCog className="w-4 h-4 shrink-0" />
      <span data-testid="text-impersonation-banner-message">
        You're viewing as <strong>{user.firstName} {user.lastName}</strong>
        {" "}({user.email}) — impersonated by {admin.firstName} {admin.lastName}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-950/40 bg-amber-500 text-amber-950 hover:bg-amber-600"
        disabled={returnToMyAccount.isPending}
        onClick={() =>
          returnToMyAccount.mutate(undefined, {
            onError: (err) => {
              toast({
                title: "Couldn't end impersonation",
                description: err instanceof Error ? err.message : "Please try again.",
                variant: "destructive",
              });
            },
          })
        }
        data-testid="button-return-to-my-account"
      >
        {returnToMyAccount.isPending ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : null}
        Return to my account
      </Button>
    </div>
  );
}
